import { useDispatch, useSelector } from 'react-redux'
import {
    DashboardSyncEvents,
    DashboardSyncSettings,
    IWidget,
    WidgetProps,
    WidgetSettings
} from '../../widgetDashboard.interface'
import {
    forwardRef,
    memo,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react'
import { selectChartTimeBucket } from '../../../../selectors/chart'
import { selectActiveWidget } from '../../../../selectors/widgetDashboard'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { selectSnapshot } from '../../../../selectors/security'
import { deleteChartConfig, setActiveChart, updateChartTimeBucket } from '../../../../reducer/chart.reducer'
import { updateWidgetSettings, updateWidgetType } from '../../../../reducer/widgetDashboard.reducer'
import { AppContext } from '../../../../App'
import { fetchOptionChainV2Initial } from '../../../../util/options'
import { selectUnderlyingOptionsTradeData } from '../../../../selectors/options'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { createStringDateFromStamp } from '../../../../util/tradePage'
import { Segment } from '../../../../proto/generated/security'
import { Box, Button, Stack, Typography } from '@mui/material'
import OiChartNew, { OiWidgetDataType } from './oiChartNew'
import { WidgetTypes } from '../../widgetDashboard.enums'
import { selectActiveLayoutId } from '../../../../selectors/home'
import { optionsTradesDataType } from '../../../../pages/typings/options.typings'
import { isExpressionMode } from '../../../../util/expression'

export type ChartWidgetSettings = WidgetSettings<{
    chartId: string
}>

const OiWidget = forwardRef<any, WidgetProps<ChartWidgetSettings>>(({
    dashboardId,
    slotId,
    settings,
    allowSplit
}, ref) => {
    const dispatch = useDispatch()
    const chartId = settings.chartId ?? `${dashboardId}-${slotId}`
    const appContext = useContext(AppContext)
    const setPrimarySecurity = useSetPrimarySecurity()
    const mounted = useRef(false)
    const removed = useRef(false)
    const { key, expressionMode, securities } = settings
    const [loading, setLoading] = useState<boolean>(true);
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => {
        if (!key) return
        let cancelled = false
        if (isExpressionMode(key)) setLoading(false);
        getSecurityAsync(key).then(async (newSecurity) => {
            if (cancelled) return
            if (!newSecurity) {
                setLoading(false)
                setSecurity(undefined)
                return
            }
            if ([Segment.FO, Segment.CD].includes(newSecurity.segment)) {
                const underlying = await getSecurityAsync(getSecurityKey(newSecurity.exchange, newSecurity.underlyingSecurityCode ?? ''))
                if (!cancelled) setSecurity(underlying ?? newSecurity)
            } else {
                if (!cancelled) setSecurity(newSecurity)
            }
        })
        return () => { cancelled = true }
    }, [key])

    const underlyingToken = useMemo(() => {
        return getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    }, [security])
    const securitySnapShot = useSelector(selectSnapshot(underlyingToken))
    const activeWidget = useSelector(selectActiveWidget(dashboardId))
    const chartBucket = useSelector(selectChartTimeBucket(chartId))
    const previousSecurityInfo = useRef({ key, expressionMode, securities })
    const previousChartBucket = useRef(chartBucket)
    const [data, setData] = useState<OiWidgetDataType[]>([])
    const [atmStrike, setAtmStrike] = useState<number>()
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken || key))
    const [selectedExpiry, setSelectedExpiry] = useState<{ expirySeconds: string, expiry: string }>()
    const [selectedExpiryState, setSelectedExpiryState] = useState<{ expirySeconds: string, expiry: string }>()
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const expiryMenu = useMemo(() => {
        return optionExpiries?.filter(ex => !ex.derived).map(expiry => {
            return {
                value: expiry.expirySeconds ?? '',
                label: createStringDateFromStamp(Number(expiry.expirySeconds))
            }
        })
    }, [optionExpiries])

    useEffect(() => {
        if (selectedExpiry && JSON.stringify(selectedExpiryState) !== JSON.stringify(selectedExpiry)) {
            setSelectedExpiryState(selectedExpiry)
        }
    }, [selectedExpiry])

    const fetchNewData = useCallback(async () => {
        setLoading(true)
        setData([])
        if (!security || !security?.hasFo) return
        let expiriesData = [...(optionExpiries ?? [])]
        let currentSelectedExpiry = selectedExpiry
        if (expiriesData.length <= 0 && currentSelectedExpiry?.expirySeconds) {
            const {optionsData} = await fetchOptionChainV2Initial(security, dispatch)
            expiriesData = optionsData as optionsTradesDataType[]
            if (expiriesData.length === 0) return
            let currentSelectedExpiry = {
                expirySeconds: expiriesData[0]?.expirySeconds ?? '',
                expiry: expiriesData[0]?.expiration?.name
            }
            setSelectedExpiry(currentSelectedExpiry)
        }
        const index = expiriesData.findIndex(o => o.expirySeconds === currentSelectedExpiry?.expirySeconds)
        const returnValue: OiWidgetDataType[] = []
        if (index !== -1) {
            let tradesData = expiriesData[index].trades
            if (!tradesData.length) {
                const { optionsData: newData } = await fetchOptionChainV2Initial(security, dispatch, true, [Number(currentSelectedExpiry?.expirySeconds)], expiriesData)
                tradesData = newData[index].trades
            }
            tradesData.forEach((data) => {
                returnValue.push({
                    time: data.strikeCol.strike / (security.priceMultiplier ?? 100),
                    values: [data?.callOption?.openInterest ?? 0, data.putOption?.openInterest ?? 0]
                })
            })
        }
        setData(returnValue)
        setAtmStrike((expiriesData[index]?.atmStrike ?? 0) / (security.priceMultiplier ?? 100))
        setLoading(false)
    }, [dispatch, optionExpiries, securities, security, securitySnapShot, selectedExpiry])

    useEffect(() => {
        fetchNewData()
    }, [security, selectedExpiryState])

    useEffect(() => {
        if (!security || !security?.hasFo) return
        let cancelled = false
        ;(async () => {
            let expiriesData = [...(optionExpiries ?? [])]
            if (expiriesData.length <= 0) {
                const { optionsData } = await fetchOptionChainV2Initial(security, dispatch)
                if (cancelled) return
                expiriesData = optionsData ?? []
            }
            const currentSelectedExpiry = {
                expirySeconds: expiriesData[0]?.expirySeconds ?? '',
                expiry: expiriesData[0]?.expiration?.name ?? ''
            }
            if (!cancelled) setSelectedExpiry(currentSelectedExpiry)
        })()
        return () => { cancelled = true }
    }, [security, optionExpiries, dispatch])

    useEffect(() => {
        const mount = !!security;

        if (activeWidget?.slotId === slotId && mount && !mounted.current && chartBucket) {
            mounted.current = true
            previousChartBucket.current = chartBucket
            dispatch(setActiveChart({
                chartId,
                chartFocused: true
            }))
            setPrimarySecurity(underlyingToken, [security], expressionMode, security?.displayName ?? security?.symbol)
        }
    }, [underlyingToken, security, activeWidget, slotId, expressionMode, chartId, chartBucket])

    useImperativeHandle<any, IWidget>(ref, () => {
        return {
            onBlur() {
            },
            onActive() {
                if (!security) return;
                setTimeout(() => {
                    dispatch(setActiveChart({
                        chartId,
                        chartFocused: true
                    }))
                    setPrimarySecurity(underlyingToken, [security], expressionMode, security?.displayName ?? security?.symbol)
                }, 100)
            },
            updateSettings(settings: WidgetSettings<any>) {
                dispatch(updateWidgetSettings({
                    id: dashboardId,
                    slotId,
                    settings
                }))
            },
            onSyncSettingsChanged(settingKey: keyof DashboardSyncSettings, value: boolean) {
                switch (settingKey) {
                    case 'security':
                        value && underlyingToken && appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                            key: underlyingToken,
                            expressionMode,
                            securities: [underlyingToken]
                        })
                        break
                    case 'timeBucket':
                        value && appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME_BUCKET, previousChartBucket.current)

                }
            },
            onRemove() {
                removed.current = true
            },
            onSecurityUpdate: (data) => {
                dispatch(updateWidgetSettings({
                    id: dashboardId,
                    slotId,
                    settings: {
                        ...data
                    }
                }))
            },
            onTimeBucketUpdate: (timeBucket) => {
                timeBucket && dispatch(updateChartTimeBucket({
                    viewKey: chartId,
                    timeBucket
                }))
            },
            onCrosshairUpdate: ([price, time]) => {
            }
        }
    }, [chartId, underlyingToken, security, expressionMode, slotId, activeWidget])

    useEffect(() => {
        if (!activeWidget || activeWidget.slotId !== slotId) return
        if (key && key !== previousSecurityInfo.current.key) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                key,
                expressionMode,
                securities
            })
            previousSecurityInfo.current = {
                key, expressionMode, securities
            }
        }
    }, [slotId, key, expressionMode, securities, activeWidget])


    useEffect(() => {
        if (!previousChartBucket.current || !chartBucket) return

        if (previousChartBucket.current.code !== chartBucket.code) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME_BUCKET, chartBucket)
            previousChartBucket.current = { ...chartBucket }
        }
    }, [chartBucket])

    useEffect(() => {
        return () => {
            if (removed.current) {
                dispatch(deleteChartConfig(chartId))
            }
        }
    }, [])

    if (!chartId) return null
    return security?.hasFo ? <OiChartNew
        chartId={chartId}
        allowSplit={allowSplit}
        loading={loading}
        data={data}
        atmStrike={atmStrike}
        security={security}
        expiryName={Number(selectedExpiry?.expirySeconds) ?? 0}
        expiryMenu={expiryMenu ?? []}
        setSelectedExpiry={setSelectedExpiry}
        selectedExpiry={selectedExpiry}
    /> : <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            background: (theme) => theme.palette.background.paper
        }}>
        <Stack gap={2}>
            {loading ? null : <>
            <Typography>F&O not present for this security</Typography>
            <Button variant={'outlined'} onClick={() => {
                dispatch(updateWidgetType({
                    id: activeLayoutId,
                    slotId: slotId,
                    type: WidgetTypes.CHART
                }))
            }
            }>Go to candle chart</Button></>
        }
        </Stack>
    </Box>
})

OiWidget.displayName = 'OiWidget'

export default memo(OiWidget)